import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEditor } from "@grapesjs/react";
import { Component as GrapesComponent } from "grapesjs";
import { AIBlockEditor } from "features/campaigns";
import { customJsScript } from "features/lightningLanders/constants";
import { addOrUpdateScriptTag, getCanvasDocument } from "features/lightningLanders/utils";
import { useNotification } from "Hooks";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { ReactComponent as AiIcon } from "Assets/icons/menu/ai.svg";

interface LightningLandersAIEditProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  selectedComponent: GrapesComponent | null;
  onApplied?: () => void;
}

function LightningLandersAIEdit({
  open,
  onClose,
  projectId,
  selectedComponent,
  onApplied,
}: LightningLandersAIEditProps) {
  const editor = useEditor();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInstructions("");
  }, [selectedComponent]);

  const selectedName = useMemo(() => {
    if (!selectedComponent) {
      return "";
    }
    return selectedComponent.getName?.() || selectedComponent.get("name") || selectedComponent.getId();
  }, [selectedComponent]);

  const selectorSummary = useMemo(() => {
    if (!selectedComponent) {
      return "";
    }
    if (typeof selectedComponent.getSelectorsString === "function") {
      return selectedComponent.getSelectorsString();
    }
    return "";
  }, [selectedComponent]);

  const handleApply = useCallback(async () => {
    if (!editor || !selectedComponent) {
      showErrorNotification("Select a component before using AI edit.");
      return;
    }
    const trimmedPrompt = instructions.trim();
    if (!trimmedPrompt) {
      showErrorNotification("Add some instructions for the AI.");
      return;
    }

    setLoading(true);
    try {
      const html = selectedComponent.toHTML() ?? "";
      const css =
        editor.getCss({
          component: selectedComponent,
          keepUnusedStyles: true,
        }) ?? "";
      // Use global script (same source as save) so AI has full page JS context
      const canvasDoc = getCanvasDocument(editor);
      const existingScriptEl = canvasDoc?.querySelector(`script[${customJsScript}]`);
      const js = (existingScriptEl?.textContent ?? "").trim();

      const block = await AIBlockEditor(projectId, trimmedPrompt, {
        html,
        css,
        js,
      });

      if (!block) {
        throw new Error("AI did not return an update for this block.");
      }

      // Capture full CSS before any changes. replaceWith() can clear rules associated
      // with the old component, so we restore and merge instead of only adding block.css.
      const currentFullCss =
        editor.getCss({ keepUnusedStyles: true }) || "";

      // Group all operations into a single undo step
      editor.UndoManager.start();

      try {
        let newComponent: GrapesComponent | null = null;

        // Only replace HTML if block.html is not empty
        if (block.html && block.html.trim() !== "") {
          const replacement = selectedComponent.replaceWith(block.html);
          newComponent = Array.isArray(replacement)
            ? (replacement[0] as GrapesComponent | undefined) || null
            : (replacement as GrapesComponent | null);
        } else {
          // If no HTML replacement, keep the original component
          newComponent = selectedComponent;
        }

        // Restore full CSS so existing rules (e.g. #inc-categories, #inc-categories-container)
        // are back after replaceWith.
        editor.setStyle(currentFullCss);

        // Add AI-returned CSS so new/updated rules apply (same approach as block drop).
        if (block.css && block.css.trim() !== "") {
          editor.Css.addRules(block.css.trim());
          // editor.render();
        }

        if (block.js && block.js.trim() !== "") {
          const canvasDoc = getCanvasDocument(editor);
          if (canvasDoc) {
            const existingScript = canvasDoc.querySelector(`script[${customJsScript}]`);
            const existingJs = existingScript ? existingScript.textContent ?? "" : "";
            const newJs = existingJs ? `${existingJs}\n\n${block.js.trim()}` : block.js.trim();
            addOrUpdateScriptTag(canvasDoc, customJsScript, newJs);
          }
        }

        if (newComponent) {
          editor.select(newComponent);
        }

        // End the undo group - all operations above will be a single undo step
        editor.UndoManager.stop();
      } catch (error) {
        // If something goes wrong, cancel the undo group
        editor.UndoManager.stop();
        throw error;
      }

      setInstructions("");
      showSuccessNotification("Updated the block with AI edits.");
      onApplied?.();
    } catch (error: any) {
      console.error("Failed to run AI block editor", error);
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to apply AI edits.";
      showErrorNotification(message);
    } finally {
      setLoading(false);
    }
  }, [
    editor,
    instructions,
    onApplied,
    projectId,
    selectedComponent,
    showErrorNotification,
    showSuccessNotification,
  ]);

  if (!open) return null;

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle><AiIcon width={20} height={20} /> Edit with AI</DialogTitle>
      <DialogContent>
        {!selectedComponent ? (
          <Typography color="text.secondary">Select an element to edit with AI.</Typography>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {selectedName || "Unnamed component"}
              </Typography>
              {selectorSummary && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {selectorSummary}
                </Typography>
              )}
            </Box>

            <TextField
              label="What should AI change?"
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              multiline
              minRows={4}
              fullWidth
              placeholder="e.g. Make the background blue and increase the heading size."
            />

            <Box>
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={loading || !instructions.trim()}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {loading ? "Applying..." : "Apply AI Edit"}
              </Button>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </CustomDialog>
  );
}

export default LightningLandersAIEdit;

